import { EntityTypes } from 'models/entity';

/**
 * The entity field a rich-text editor binds. `field` is the lowercase DTO property name the API's
 * field table keys on ('description', 'analysis', 'strategy', ...).
 */
export type CopilotFieldRef = {
  entityType: EntityTypes;
  entityId: number;
  field: string;
};

// The field ref plus the label the scene shows for it, so a prompt names the field the way the user sees it, and
// the record as the user names it (a control's code and name, a risk's name) for the title of the field's session.
export type CopilotFieldBinding = CopilotFieldRef & { label: string; entityName?: string };

// What travels with a field-scoped request: the field and the text the editor holds right now, saved or not.
export type CopilotActiveField = CopilotFieldRef & { text: string; entityName?: string };

// How the user applies a proposed text to the field. Discard needs no delivery.
export type CopilotFieldProposalDecision = 'insert' | 'replace';

type FillInField = { entityType: EntityTypes; field: string };

/**
 * The (entity type, field) pairs the API's field table gives a fill-in question; mirrors
 * `FieldCompletionFields.cs`. A pair missing here gets no fill-in action, so the editor does not offer
 * what the server refuses. Requirement fields resolve to Control, as the server does.
 */
const aiFillInFields: readonly FillInField[] = [
  { entityType: EntityTypes.Risk, field: 'name' },
  { entityType: EntityTypes.Risk, field: 'description' },
  { entityType: EntityTypes.Risk, field: 'analysis' },
  { entityType: EntityTypes.Risk, field: 'strategy' },
  { entityType: EntityTypes.Risk, field: 'asset' },
  { entityType: EntityTypes.Objective, field: 'description' },
  { entityType: EntityTypes.Process, field: 'description' },
  { entityType: EntityTypes.Control, field: 'implementation' },
  { entityType: EntityTypes.Control, field: 'background' },
  { entityType: EntityTypes.Asset, field: 'description' },
  { entityType: EntityTypes.ProcessingActivity, field: 'purpose' },
  { entityType: EntityTypes.ProcessingActivity, field: 'description' },
];

// The API keeps a session's origin field trimmed and lowercased, so both sides compare in that form.
const normalizeFieldName = (field: string): string => field.trim().toLowerCase();

// Whether two refs name the same field of the same record: a field's session continues for that field only.
export const isSameField = (a: CopilotFieldRef, b: CopilotFieldRef): boolean =>
  a.entityType === b.entityType &&
  a.entityId === b.entityId &&
  normalizeFieldName(a.field) === normalizeFieldName(b.field);

export const hasAIFillInField = (ref: CopilotFieldRef): boolean => {
  const entityType = ref.entityType === EntityTypes.Requirement ? EntityTypes.Control : ref.entityType;

  return aiFillInFields.some((item) => item.entityType === entityType && item.field === ref.field);
};
